import { apiClient } from '../../services/api'

export type Industry = 'hospital' | 'clinic' | 'sme' | 'other'
export type ClientStatus = 'active' | 'suspended'
export type ClientRole = 'owner' | 'admin' | 'marketer' | 'analyst' | 'viewer'

export interface Client {
  id: string
  name: string
  slug: string
  industry: Industry
  status: ClientStatus
  avatar_url: string | null
  created_at: string
  current_user_role: ClientRole | null
}

export interface ClientMember {
  id: string
  client_id: string
  user_id: string
  user_email: string | null
  user_name: string | null
  role: ClientRole
  created_at: string
}

export interface CreateClientPayload {
  name: string
  slug: string
  industry?: Industry
}

export interface UpdateClientPayload {
  name?: string
  industry?: Industry
  status?: ClientStatus
}

export interface AddMemberPayload {
  email: string
  role?: ClientRole
  name?: string
  password?: string
}

export async function listClients(): Promise<Client[]> {
  const { data } = await apiClient.get<Client[]>('/clients')
  return data
}

export async function getClient(clientId: string): Promise<Client> {
  const { data } = await apiClient.get<Client>(`/clients/${clientId}`)
  return data
}

export async function createClient(
  payload: CreateClientPayload,
): Promise<Client> {
  const { data } = await apiClient.post<Client>('/clients', payload)
  return data
}

export async function updateClient(
  clientId: string,
  payload: UpdateClientPayload,
): Promise<Client> {
  const { data } = await apiClient.patch<Client>(
    `/clients/${clientId}`,
    payload,
  )
  return data
}

export async function deleteClient(clientId: string): Promise<void> {
  await apiClient.delete(`/clients/${clientId}`)
}

export async function uploadClientAvatar(
  clientId: string,
  file: File,
): Promise<Client> {
  const form = new FormData()
  form.append('file', file)
  const { data } = await apiClient.post<Client>(
    `/clients/${clientId}/avatar`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return data
}

export async function listMembers(clientId: string): Promise<ClientMember[]> {
  const { data } = await apiClient.get<ClientMember[]>(
    `/clients/${clientId}/members`,
  )
  return data
}

export async function addMember(
  clientId: string,
  payload: AddMemberPayload,
): Promise<ClientMember> {
  const { data } = await apiClient.post<ClientMember>(
    `/clients/${clientId}/members`,
    payload,
  )
  return data
}

export async function removeMember(clientId: string, userId: string): Promise<void> {
  await apiClient.delete(`/clients/${clientId}/members/${userId}`)
}
